import { STARTKAPITAL } from '@/config';
import { bookCatalog } from '@/data/bookCatalog';
import { KRITIKERE, type KritikerId, type TerningTall } from '@/data/kritikere';
import { NORSKE_ETTERNAVN, NORSKE_FORNAVN } from '@/data/names';
import type {
  Anmeldelse,
  Author,
  AuthorStats,
  ContractedBook,
  Manuscript,
} from '@/data/types';
import { mulberry32 } from '@/data/bookCatalog';
import type { GameState, GameStateData, Tier } from './GameState';

// Runde 48 (Sven): arven trimmet til 2 forfattere — én D-tier backlist-bok
// (under) + den S-tier-boka «Mord på Skrivekurset» i produksjon. Tidligere
// startet vi med 4 backlist-bøker + S-tier (5 forfattere), som føltes for
// fullt for en indie-start.
const BJORN_SEED_ISBN: ReadonlyArray<string> = [
  '9788242983442',
];

const BJORN_PROD_ISBN = '9788202875176';
const BJORN_PROD_TITTEL = 'Mord på Skrivekurset';

const SITAT_ARV = [
  'En arvet utgivelse som fortsatt finner lesere.',
  'Et tidstypisk småforlags-prosjekt.',
  'Sjangerkonvensjonelt, men leverer det den lover.',
  'Stillferdig, kompetent — en bok du nesten har glemt.',
  'Lavmælt og solid håndverk.',
];

const KRITIKER_IDS: ReadonlyArray<KritikerId> = [
  'klassekampen',
  'aftenposten',
  'morgenbladet',
  'nrk-bok',
];

export function erFersktSave(state: GameState): boolean {
  return (
    state.dag === 1 &&
    state.kroner === STARTKAPITAL &&
    state.utgitteBoker.length === 0 &&
    state.iProduksjon.length === 0 &&
    Object.keys(state.authors).length === 0
  );
}

export function bootstrapBjornArvHvisNy(
  state: GameState,
  rng: () => number = mulberry32(99001),
): boolean {
  if (!erFersktSave(state)) return false;
  if (!bookCatalog.isReady) return false;

  const data = state.snapshot;
  const arvAuthors: Author[] = [];
  const arvBoker: ContractedBook[] = [];

  for (const isbn of BJORN_SEED_ISBN) {
    const seed = finnSeedBokFraKatalog(isbn);
    if (!seed) continue;
    const forfatter = lagArvForfatter(seed, 'D', rng);
    arvAuthors.push(forfatter);
    const bok = lagArvBok(seed, forfatter, rng);
    arvBoker.push(bok);
  }

  const stier = lagSTierBokOgForfatter(rng);
  if (stier) {
    arvAuthors.push(stier.forfatter);
    arvBoker.push(stier.bok);
  }

  const ferskAuthors: Record<string, Author> = {};
  for (const a of arvAuthors) ferskAuthors[a.id] = a;

  const utgitte: ContractedBook[] = [];
  const iProd: ContractedBook[] = [];
  for (const b of arvBoker) {
    if (b.status === 'utgitt') utgitte.push(b);
    else iProd.push(b);
  }

  const oppdatert: GameStateData = {
    ...data,
    authors: ferskAuthors,
    utgitteBoker: utgitte,
    iProduksjon: iProd,
  };
  state.applySaveSnapshot(oppdatert);
  return true;
}

type SeedManus = Pick<Manuscript, 'isbn' | 'title' | 'author' | 'genre' | 'tier' | 'editorial_blurb' | 'cover_url'>;

function finnSeedBokFraKatalog(isbn: string): SeedManus | null {
  const treff = bookCatalog.findByIsbn(isbn);
  if (!treff) return null;
  return {
    isbn: treff.isbn,
    title: treff.title,
    author: treff.author,
    genre: treff.genre,
    tier: treff.tier,
    editorial_blurb: treff.editorial_blurb,
    cover_url: treff.cover_url,
  };
}

function lagArvForfatter(seed: SeedManus, tier: Tier, rng: () => number): Author {
  const stats = lagStatsForTier(tier, rng);
  const erUkjent = !seed.author || seed.author.trim().toLowerCase() === 'ukjent forfatter';
  const navn = erUkjent ? lagPseudonym(rng) : seed.author;
  return {
    id: lagArvId(seed.isbn),
    navn,
    pseudonym: erUkjent,
    primaerSjanger: seed.genre,
    stats,
    lojalitet: 55,
    stress: 25,
    ego: 40,
      drikkenivaa: 20,
    karriere: [],
    lojalitetHistorikk: [],
  };
}

function lagArvBok(seed: SeedManus, forfatter: Author, rng: () => number): ContractedBook {
  const salg = Math.round(800 + rng() * 1700);
  const anmeldelser = lagArvAnmeldelser(seed.isbn, rng);
  const inntekt = salg * 150;
  return {
    isbn: seed.isbn,
    title: seed.title,
    author: forfatter.navn,
    genre: seed.genre,
    tier: 'D',
    editorial_blurb: seed.editorial_blurb,
    cover_url: seed.cover_url,
    status: 'utgitt',
    forskudd: 20_000,
    dagKontrahert: -180,
    authorId: forfatter.id,
    faseStartDag: -90,
    faseFerdigDag: null,
    utgivelsesDag: -60,
    anmeldelser,
    salg,
    inntekt,
    dagerUtenSalg: 8,
    milestonerNaadd: salg >= 1000 ? [1000] : [],
  };
}

function lagArvAnmeldelser(isbn: string, rng: () => number): Anmeldelse[] {
  const antall = 2 + Math.floor(rng() * 3);
  const utvalg = [...KRITIKER_IDS].sort(() => rng() - 0.5).slice(0, antall);
  const ut: Anmeldelse[] = [];
  for (let i = 0; i < utvalg.length; i += 1) {
    const kritikerId = utvalg[i];
    const terninger = (2 + Math.floor(rng() * 4)) as TerningTall;
    const sitat = SITAT_ARV[Math.floor(rng() * SITAT_ARV.length)];
    ut.push({
      kritikerId,
      terninger,
      sitat: `${sitat} — ${KRITIKERE[kritikerId].publikasjon}`,
      dag: -50 + i * 3,
    });
  }
  void isbn;
  return ut;
}

function lagSTierBokOgForfatter(
  rng: () => number,
): { bok: ContractedBook; forfatter: Author } | null {
  const seed = finnSeedBokFraKatalog(BJORN_PROD_ISBN);
  if (!seed) return null;
  const forfatter = lagArvForfatter(seed, 'S', rng);
  const bok: ContractedBook = {
    isbn: seed.isbn,
    title: BJORN_PROD_TITTEL,
    author: forfatter.navn,
    genre: seed.genre,
    tier: 'S',
    editorial_blurb: seed.editorial_blurb,
    cover_url: seed.cover_url,
    status: 'designet',
    forskudd: 220_000,
    dagKontrahert: -45,
    authorId: forfatter.id,
    faseStartDag: -5,
    faseFerdigDag: null,
    utgivelsesDag: null,
    anmeldelser: [],
    salg: 0,
    inntekt: 0,
    dagerUtenSalg: 0,
    milestonerNaadd: [],
  };
  return { bok, forfatter };
}

function lagStatsForTier(tier: Tier, rng: () => number): AuthorStats {
  const range = tier === 'S' ? { min: 75, max: 85 } : { min: 45, max: 55 };
  const stat = (): number =>
    Math.round(range.min + rng() * (range.max - range.min));
  return {
    dialog: stat(),
    plot: stat(),
    karakter: stat(),
    spraak: stat(),
    voice: stat(),
    autentisitet: stat(),
    disiplin: stat(),
  };
}

function lagPseudonym(rng: () => number): string {
  const f = NORSKE_FORNAVN[Math.floor(rng() * NORSKE_FORNAVN.length)];
  const e = NORSKE_ETTERNAVN[Math.floor(rng() * NORSKE_ETTERNAVN.length)];
  return `${f} ${e}`;
}

function lagArvId(isbn: string): string {
  return `arv-${isbn}`;
}

export const _BJORN_TEST_HOOKS = {
  BJORN_SEED_ISBN,
  BJORN_PROD_ISBN,
  BJORN_PROD_TITTEL,
};
